import { useRef } from "react";
import { layout } from "@/lib/design";

const M = 10; // margin (inches) for dimensions

function Symbol({ s }) {
  const { x, y, w, h, node } = s;
  const p = 2, X1 = x + p, Y1 = y + p, X2 = x + w - p, Y2 = y + h - p, cx = x + w / 2, cy = y + h / 2;
  const dash = { stroke: "#171A20", strokeWidth: 0.35, strokeDasharray: "1.6 1.2", fill: "none" };
  const k = node.type;
  const side = (hinge) => (hinge === "right" ? [X1, X2] : [X2, X1]);
  const out = [];
  if (k === "casement" || k === "tilt_turn" || k === "door") {
    const [far, hx] = side(node.hinge);
    out.push(<polyline key="c" points={`${far},${Y1} ${hx},${cy} ${far},${Y2}`} {...dash} />);
  }
  if (k === "awning" || k === "tilt_turn") out.push(<polyline key="a" points={`${X1},${Y2} ${cx},${Y1} ${X2},${Y2}`} {...dash} />);
  if (k === "hopper") out.push(<polyline key="h" points={`${X1},${Y1} ${cx},${Y2} ${X2},${Y1}`} {...dash} />);
  if (k === "sliding") {
    const [a, b] = node.slide === "left" ? [X2 - w * 0.3, X1 + w * 0.3] : [X1 + w * 0.3, X2 - w * 0.3];
    const dir = Math.sign(b - a);
    out.push(<g key="s" stroke="#171A20" strokeWidth="0.5"><line x1={a} y1={cy} x2={b} y2={cy} /><polyline points={`${b - dir * 2.5},${cy - 1.8} ${b},${cy} ${b - dir * 2.5},${cy + 1.8}`} fill="none" /></g>);
  }
  if (k === "door") out.push(<text key="d" x={cx} y={Y1 + 4} fontSize="3" textAnchor="middle" fill="#171A20">{node.swing === "out" ? "OUT" : "IN"}</text>);
  if (node.grid) {
    const r = Number(node.grid.rows) || 2, c = Number(node.grid.cols) || 2;
    for (let i = 1; i < c; i++) out.push(<line key={`gc${i}`} x1={X1 + ((X2 - X1) * i) / c} y1={Y1} x2={X1 + ((X2 - X1) * i) / c} y2={Y2} stroke="#8C6B30" strokeWidth="0.45" />);
    for (let i = 1; i < r; i++) out.push(<line key={`gr${i}`} x1={X1} y1={Y1 + ((Y2 - Y1) * i) / r} x2={X2} y2={Y1 + ((Y2 - Y1) * i) / r} stroke="#8C6B30" strokeWidth="0.45" />);
  }
  out.push(<text key="l" x={cx} y={cy + 1.4} fontSize="3.6" textAnchor="middle" fill="#0062CC" fontWeight="600">{k === "fixed" ? "O" : "X"}</text>);
  return out;
}

const fmt = (v) => `${Math.round(v * 8) / 8}"`;

export function DesignCanvas({ design, selected, onSelect, onDrag, onDragEnd, shapes = {}, interactive = true, className = "", testId = "design-canvas" }) {
  const svg = useRef(null);
  const drag = useRef(null);
  const L = layout(design);
  const W = Number(design.w) || 1, H = Number(design.h) || 1;
  const toIn = (e) => {
    const pt = svg.current.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svg.current.getScreenCTM().inverse());
  };
  const down = (dv) => (e) => {
    if (!interactive) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { dv, start: toIn(e) };
  };
  const move = (e) => {
    if (!drag.current) return;
    const p = toIn(e), { dv, start } = drag.current;
    onDrag?.(dv, dv.cols ? p.x - start.x : p.y - start.y);
  };
  const up = () => { if (drag.current) { drag.current = null; onDragEnd?.(); } };
  return (
    <svg ref={svg} data-testid={testId} viewBox={`${-M / 2} ${-M / 2} ${W + M * 1.6} ${H + M * 1.6}`} className={`touch-none select-none ${className}`}
      onPointerMove={move} onPointerUp={up} onPointerCancel={up} preserveAspectRatio="xMidYMid meet">
      {L.units.map((u) => (
        <g key={`u${u.path.join()}`}>
          <rect x={u.x} y={u.y} width={u.w} height={u.h} fill="#F2F2F7" stroke="#171A20" strokeWidth="1.4" />
          {u.node.shape_id && shapes[u.node.shape_id] && (
            <path d={`M ${u.x + 1.5} ${u.y + Math.min(u.w / 2, u.h * 0.6)} A ${u.w / 2 - 1.5} ${Math.min(u.w / 2, u.h * 0.6) - 1.5} 0 0 1 ${u.x + u.w - 1.5} ${u.y + Math.min(u.w / 2, u.h * 0.6)}`}
              fill="none" stroke="#007AFF" strokeWidth="0.8" data-testid="design-shape-arc" />
          )}
        </g>
      ))}
      {L.secs.map((s, i) => {
        const on = selected && s.path.join() === selected.join();
        return (
          <g key={`s${s.path.join()}`} data-testid={`design-section-${i}`} onClick={() => interactive && onSelect?.(s.path)} className={interactive ? "cursor-pointer" : ""}>
            <rect x={s.x + 1} y={s.y + 1} width={Math.max(s.w - 2, 0)} height={Math.max(s.h - 2, 0)} fill={on ? "rgba(0,122,255,0.18)" : "rgba(190,215,230,0.45)"}
              stroke={on ? "#007AFF" : "#171A20"} strokeWidth={on ? 0.9 : 0.5} />
            <Symbol s={s} />
            {interactive && <text x={s.x + s.w / 2} y={s.y + s.h - 2.5} fontSize="2.6" textAnchor="middle" fill="#3A3A3C">{fmt(s.w)} × {fmt(s.h)}</text>}
          </g>
        );
      })}
      {L.dividers.map((dv, i) => (
        <g key={`d${i}`}>
          {dv.mull && (dv.cols ? <rect x={dv.x - 0.9} y={dv.y} width="1.8" height={dv.len} fill="#171A20" /> : <rect x={dv.x} y={dv.y - 0.9} width={dv.len} height="1.8" fill="#171A20" />)}
          {interactive && (
            <rect data-testid={`design-divider-${i}`} onPointerDown={down(dv)} fill="rgba(168,130,63,0.001)" className={dv.cols ? "cursor-col-resize" : "cursor-row-resize"}
              x={dv.cols ? dv.x - 2.5 : dv.x} y={dv.cols ? dv.y : dv.y - 2.5} width={dv.cols ? 5 : dv.len} height={dv.cols ? dv.len : 5} />
          )}
        </g>
      ))}
      <g stroke="#171A20" strokeWidth="0.3" fontSize="3.4" fill="#171A20">
        <line x1="0" y1={H + 4} x2={W} y2={H + 4} /><line x1="0" y1={H + 2.5} x2="0" y2={H + 5.5} /><line x1={W} y1={H + 2.5} x2={W} y2={H + 5.5} />
        <text x={W / 2} y={H + 9} textAnchor="middle" stroke="none" data-testid="design-dim-width">{fmt(W)}</text>
        <line x1={W + 4} y1="0" x2={W + 4} y2={H} /><line x1={W + 2.5} y1="0" x2={W + 5.5} y2="0" /><line x1={W + 2.5} y1={H} x2={W + 5.5} y2={H} />
        <text x={W + 9} y={H / 2} textAnchor="middle" stroke="none" transform={`rotate(-90 ${W + 9} ${H / 2})`} data-testid="design-dim-height">{fmt(H)}</text>
      </g>
    </svg>
  );
}
